import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useI18n } from '../hooks/useI18n';

// 导入新的图片资源
import jiaoLianIcon from '../assets/images/tabbar/new/jiaolian.png';
import jiaoLianActiveIcon from '../assets/images/tabbar/new/jiaolian_active.png';
import duDaoIcon from '../assets/images/tabbar/new/dudao.png';
import duDaoActiveIcon from '../assets/images/tabbar/new/dudao_active.png';
import woDeIcon from '../assets/images/tabbar/new/wode.png';
import woDeActiveIcon from '../assets/images/tabbar/new/wode_active.png';
import NewTrainingGuider from './NewTrainingGuider';




const NewTabBar: React.FC = () => {
  const location = useLocation();
  const { pathname } = location;
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [lastActivity, setLastActivity] = useState(Date.now());
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isNarrowScreen, setIsNarrowScreen] = useState(window.innerWidth < 400);
  const { t } = useI18n();

  const tabs = [
    {
      path: '/ai-training/list',
      icon: jiaoLianIcon,
      activeIcon: jiaoLianActiveIcon,
      label: 'AI教练'
    },
    {
      path: '/ai-supervisor/list',
      icon: duDaoIcon,
      activeIcon: duDaoActiveIcon,
      label: 'AI督导'
    },
    {
      path: '/my-setting',
      icon: woDeIcon,
      activeIcon: woDeActiveIcon,
      label: '我的'
    },
  ];

  // 检查当前路径是否为tabbar页面
  const isTabBarPage = tabs.some(tab =>
    pathname === tab.path || (tab.path !== '/' && pathname.startsWith(tab.path))
  );

  // 监听窗口大小变化
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      setIsNarrowScreen(window.innerWidth < 400);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // 监听滚动和用户活动
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      // 向下滚动时隐藏，向上滚动时显示
      if (currentScrollY > lastScrollY + 10) {
        setIsVisible(false);
      } else if (currentScrollY < lastScrollY - 10) {
        setIsVisible(true);
      }

      setLastScrollY(currentScrollY);
      setLastActivity(Date.now());
    };

    const handleActivity = () => {
      setLastActivity(Date.now());
      setIsVisible(true);
    };

    // 监听用户活动
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('mousemove', handleActivity);
    window.addEventListener('keydown', handleActivity);
    window.addEventListener('touchstart', handleActivity);

    // 定时检查用户是否长时间无活动
    const inactivityTimer = setInterval(() => {
      if (Date.now() - lastActivity > 300000) { // 3秒无活动自动隐藏
        setIsVisible(false);
      }
    }, 1000);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('mousemove', handleActivity);
      window.removeEventListener('keydown', handleActivity);
      window.removeEventListener('touchstart', handleActivity);
      clearInterval(inactivityTimer);
    };
  }, [lastScrollY, lastActivity]);

  // 如果不是tabbar页面，不显示
  if (!isTabBarPage) {
    return null;
  }

  return (
    <AnimatePresence>
{/* <NewTrainingGuider /> */}

      {isVisible && (
        <motion.div
          initial={{ y: 100 }}
          animate={{ y: 0 }}
          exit={{ y: 100 }}
          transition={{
            type: 'spring',
            stiffness: 300,
            damping: 30
          }}
          className={`fixed bottom-8 z-50 ${
            isNarrowScreen
              ? 'left-6 right-6'
              : isMobile
                ? 'left-20 right-20'
                : 'left-[35%] right-[35%]'
          }`}
        >
          <div className="bg-white/80 backdrop-blur-md rounded-[40px] shadow-lg h-[52px] flex items-center justify-center px-4">
            <div className="flex justify-center items-center gap-[60px] w-full">
              {tabs.map((tab) => {
                const isActive = pathname === tab.path ||
                              (tab.path !== '/' && pathname.startsWith(tab.path));

                // 为引导功能添加 data-tour 属性
                const tourId = tab.path === '/ai-training/list'
                  ? 'coach-tab'
                  : tab.path === '/ai-supervisor/list'
                  ? 'supervisor-tab'
                  : 'my-tab';

                return (
                  <Link
                    key={tab.path}
                    to={tab.path}
                    data-tour={tourId}
                    className="flex flex-col items-center justify-center"
                  >
                    <motion.div
                      className="relative flex items-center justify-center w-[28px] h-[28px] mb-[4px]"
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.95 }}
                      transition={{ type: "spring", stiffness: 400, damping: 17 }}
                    >
                      {/* 非激活状态图标 */}
                      <motion.img
                        src={tab.icon}
                        alt={tab.label}
                        className="absolute w-full h-full object-contain"
                        animate={{
                          opacity: isActive ? 0 : 1,
                          scale: isActive ? 0.8 : 1
                        }}
                        transition={{ duration: 0.2 }}
                      />

                      {/* 激活状态图标 */}
                      <motion.img
                        src={tab.activeIcon}
                        alt={tab.label}
                        className="absolute w-full h-full object-contain"
                        animate={{
                          opacity: isActive ? 1 : 0,
                          scale: isActive ? 1 : 0.8
                        }}
                        transition={{ duration: 0.2 }}
                      />
                    </motion.div>

                    {/* 标签文字 */}
                    <motion.span
                      className={`text-[9px] font-normal leading-[10px] transition-colors duration-200 ${
                        isActive ? 'text-[#0A1B39]' : 'text-[#86909C]'
                      }`}
                      style={{ fontFamily: 'PingFang SC' }}
                      animate={{
                        scale: isActive ? 1 : 1,
                        opacity: isActive ? 1 : 1
                      }}
                      transition={{ duration: 0.2 }}
                    >
                      {tab.label}
                    </motion.span>
                  </Link>
                );
              })}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default NewTabBar;
